<script setup>
import { computed } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { canSeeInstanceUpdate } from 'dashboard/components/app/versionCheckHelper';

import semver from 'semver';

const { t } = useI18n();
const { currentAccount } = useAccount();

const latestChatwootVersion = computed(() => {
  return currentAccount.value.latest_chatwoot_version;
});

const globalConfig = useMapGetter('globalConfig/get');
const currentUser = useMapGetter('getCurrentUser');

const hasAnUpdateAvailable = computed(() => {
  if (!semver.valid(latestChatwootVersion.value)) {
    return false;
  }

  return semver.lt(globalConfig.value.appVersion, latestChatwootVersion.value);
});

// Mesmo recado do UpdateBanner: pede para atualizar a instância, o que só o
// super admin faz. O administrador da conta do cliente não vê.
const showUpdateNotice = computed(
  () =>
    hasAnUpdateAvailable.value &&
    globalConfig.value.displayManifest &&
    canSeeInstanceUpdate(currentUser.value)
);

// O hash do build era texto técnico para o cliente e copiava no clique de um
// span sem foco. Fica só a versão à vista; o identificador vai num botão, para
// o suporte pedir.
const copyGitSha = async () => {
  try {
    await copyTextToClipboard(globalConfig.value.gitSha);
    useAlert(t('GENERAL_SETTINGS.BUILD.COPIED'));
  } catch {
    // Sem permissão de área de transferência: nada a copiar, nada a avisar.
  }
};
</script>

<template>
  <div class="p-4 text-sm text-center">
    <div v-if="showUpdateNotice">
      {{
        t('GENERAL_SETTINGS.UPDATE_CHATWOOT', {
          latestChatwootVersion: latestChatwootVersion,
        })
      }}
    </div>
    <div class="flex items-center justify-center gap-1 text-n-slate-11">
      <span data-testid="build-version">
        {{
          t('GENERAL_SETTINGS.BUILD.VERSION', {
            version: globalConfig.appVersion,
          })
        }}
      </span>
      <button
        v-tooltip="t('GENERAL_SETTINGS.BUILD.COPY_ID')"
        type="button"
        class="inline-flex items-center justify-center rounded-lg size-11 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
        :aria-label="t('GENERAL_SETTINGS.BUILD.COPY_ID')"
        data-testid="build-copy-id"
        @click="copyGitSha"
      >
        <span class="i-ph-copy size-4" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>
